import { useState, useCallback } from "react";
import axios from "axios";
import { QualificationStep } from "../components/quiz/QualificationStep";
import { StageQuestion } from "../components/quiz/StageQuestion";
import { LeadCaptureStep } from "../components/quiz/LeadCaptureStep";
import { ResultsPage } from "../components/quiz/ResultsPage";
import { STAGES, getTierForScore, getWeakestStages } from "../data/quizData";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Quiz flow steps
const STEPS = {
  QUALIFICATION: "qualification",
  QUIZ: "quiz",
  LEAD_CAPTURE: "lead_capture",
  RESULTS: "results"
};

export default function QuizPage() {
  const [currentStep, setCurrentStep] = useState(STEPS.QUALIFICATION);
  const [qualificationData, setQualificationData] = useState(null);
  const [currentStageIndex, setCurrentStageIndex] = useState(0);
  const [stageResults, setStageResults] = useState([]);
  const [stageAnswers, setStageAnswers] = useState({});
  const [leadData, setLeadData] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionId, setSubmissionId] = useState(null);

  // Handle qualification completion
  const handleQualified = useCallback((data) => {
    setQualificationData(data);
    setCurrentStep(STEPS.QUIZ);
  }, []);

  // Handle stage completion
  const handleStageComplete = useCallback((result) => {
    // Save answers for this stage
    setStageAnswers(prev => ({
      ...prev,
      [result.stageNumber]: result.answers
    }));

    // Update or add stage result
    setStageResults(prev => {
      const existing = prev.findIndex(r => r.stageNumber === result.stageNumber);
      if (existing >= 0) {
        const updated = [...prev];
        updated[existing] = result;
        return updated;
      }
      return [...prev, result];
    });

    // Move to next stage or lead capture
    if (currentStageIndex < STAGES.length - 1) {
      setCurrentStageIndex(prev => prev + 1);
    } else {
      setCurrentStep(STEPS.LEAD_CAPTURE);
    }
  }, [currentStageIndex]);

  // Handle back navigation
  const handleBack = useCallback(() => {
    if (currentStageIndex > 0) {
      setCurrentStageIndex(prev => prev - 1);
    } else {
      setCurrentStep(STEPS.QUALIFICATION);
      setQualificationData(null);
    }
  }, [currentStageIndex]);

  // Calculate totals
  const calculateTotals = useCallback(() => {
    const totalScore = stageResults.reduce((sum, r) => sum + r.score, 0);
    const maxTotalScore = stageResults.reduce((sum, r) => sum + r.maxScore, 0);
    return { totalScore, maxTotalScore };
  }, [stageResults]);

  // Handle lead form submission
  const handleLeadSubmit = useCallback(async (formData) => {
    setIsSubmitting(true);
    setLeadData(formData);

    const { totalScore, maxTotalScore } = calculateTotals();
    const tier = getTierForScore(totalScore);
    const weakestStages = getWeakestStages(
      stageResults.map(r => ({ 
        stageName: r.stageName, 
        score: r.score, 
        maxScore: r.maxScore 
      })),
      2
    );

    // Prepare submission data
    const submissionData = {
      name: formData.name,
      email: formData.email,
      website: formData.website || null,
      growth_challenge: formData.growthChallenge,
      business_type: qualificationData.businessType,
      monthly_revenue: qualificationData.monthlyRevenue,
      stage_scores: stageResults.map(r => ({
        stage_name: r.stageName,
        stage_number: r.stageNumber,
        score: r.score,
        max_score: r.maxScore
      })),
      total_score: totalScore,
      max_total_score: maxTotalScore,
      evaluation_tier: tier.name,
      weakest_stages: weakestStages
    };

    try {
      const response = await axios.post(`${API}/quiz/submit`, submissionData);
      console.log("Quiz submission successful");
      setSubmissionId(response.data.id);
    } catch (error) {
      console.error("Error submitting quiz:", error);
      // Continue to results even if submission fails
    }

    setIsSubmitting(false);
    setCurrentStep(STEPS.RESULTS);
  }, [calculateTotals, stageResults, qualificationData]);

  // Render current step
  const renderStep = () => {
    switch (currentStep) {
      case STEPS.QUALIFICATION:
        return (
          <QualificationStep 
            onQualified={handleQualified}
          />
        );

      case STEPS.QUIZ:
        const currentStage = STAGES[currentStageIndex];
        return (
          <StageQuestion
            stage={currentStage}
            currentStageIndex={currentStageIndex}
            totalStages={STAGES.length}
            onComplete={handleStageComplete}
            onBack={handleBack}
            initialAnswers={stageAnswers[currentStage.number] || {}}
          />
        );

      case STEPS.LEAD_CAPTURE:
        const { totalScore, maxTotalScore } = calculateTotals();
        return (
          <LeadCaptureStep
            onSubmit={handleLeadSubmit}
            totalScore={totalScore}
            maxScore={maxTotalScore}
          />
        );

      case STEPS.RESULTS:
        const totals = calculateTotals();
        return (
          <ResultsPage
            stageScores={stageResults.map(r => ({
              stageNumber: r.stageNumber,
              stageName: r.stageName,
              score: r.score,
              maxScore: r.maxScore
            }))}
            totalScore={totals.totalScore}
            maxScore={totals.maxTotalScore}
            leadData={leadData}
            businessType={qualificationData?.businessType}
            monthlyRevenue={qualificationData?.monthlyRevenue}
            submissionId={submissionId}
          />
        );

      default:
        return null;
    }
  };

  return (
    <div className="zaharlab-app" data-testid="quiz-page">
      <div className="bg-pattern" />
      <div className="bg-grid" />
      <div className="content-wrapper">
        {renderStep()}
      </div>
    </div>
  );
}
